import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Pagination } from 'react-bootstrap';
import moment from 'moment';

const DetailedScheduleListView = ({
    scheduleData,
    isScheduleLoading,
    studentFilter,
    tutorFilter,
    handleOpenEdit,
    handleDeleteRow
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [expandedKeys, setExpandedKeys] = useState({});

    // Reset pagination and expansions when filters change
    useEffect(() => {
        setCurrentPage(1);
        setExpandedKeys({});
    }, [studentFilter, tutorFilter]);

    // Grouping and Filtering logic
    const getGroupedSchedule = () => {
        const groups = {};
        
        // Filter rows first
        const filtered = (scheduleData || []).filter(row => {
            const studentMatch = !studentFilter || (row.hocVienTen || row.HocVienTen || '').toLowerCase().includes(studentFilter.toLowerCase());
            const gvs = row.lichHocThucHanh_GiangViens || row.LichHocThucHanh_GiangViens || [];
            const gvNames = gvs.map(g => g.giangVienTen || g.GiangVienTen || '').join(' ').toLowerCase();
            const tutorMatch = !tutorFilter || gvNames.includes(tutorFilter.toLowerCase());
            return studentMatch && tutorMatch;
        });

        // Group rows by period + donVi
        filtered.forEach(row => {
            const start = row.ngayBatDau || row.NgayBatDau;
            const end = row.ngayKetThuc || row.NgayKetThuc;
            const startStr = start ? moment(start).format('DD/MM/YYYY') : '';
            const endStr = end ? moment(end).format('DD/MM/YYYY') : 'Chưa kết thúc';
            const dateRangeStr = `${startStr} - ${endStr}`;
            const dept = row.donVi || row.DonVi || 'Khác';
            
            // Create a unique key for grouping
            const key = `${start}_${end}_${dept}`;
            
            if (!groups[key]) {
                groups[key] = {
                    key,
                    ngayBatDau: start,
                    ngayKetThuc: end,
                    dateRangeStr,
                    donVi: dept,
                    tutors: [],
                    students: []
                };
            }
            
            // Add student assignment to this group
            groups[key].students.push(row);
            
            // Merge tutors
            const gvs = row.lichHocThucHanh_GiangViens || row.LichHocThucHanh_GiangViens || [];
            gvs.forEach(g => {
                const gvName = g.giangVienTen || g.GiangVienTen;
                if (gvName && !groups[key].tutors.includes(gvName)) {
                    groups[key].tutors.push(gvName);
                }
            });
        });

        // Return sorted groups (ascending by start date)
        return Object.values(groups).sort((a, b) => new Date(a.ngayBatDau) - new Date(b.ngayBatDau));
    };

    const groupedData = getGroupedSchedule();
    const paginatedGroups = groupedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const totalPages = Math.ceil(groupedData.length / pageSize);

    const toggleGroup = (key) => {
        setExpandedKeys(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleExpandAll = () => {
        const allExpanded = paginatedGroups.every(g => expandedKeys[g.key]);
        const newExpanded = {};
        if (!allExpanded) {
            paginatedGroups.forEach(g => {
                newExpanded[g.key] = true;
            });
        }
        setExpandedKeys(newExpanded);
    };

    if (isScheduleLoading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary spinner-border-sm mr-2"></div> Đang tải lịch học...
            </div>
        );
    }

    const allExpanded = paginatedGroups.length > 0 && paginatedGroups.every(g => expandedKeys[g.key]);

    return (
        <>
            <style>{`
                .parent-row {
                    cursor: pointer;
                    background-color: #fafafa;
                    transition: background-color 0.15s ease;
                }
                .parent-row:hover {
                    background-color: #f1f5f9 !important;
                }
                .toggle-icon {
                    transition: transform 0.2s ease;
                    color: #64748b;
                }
                .toggle-icon.expanded {
                    transform: rotate(90deg);
                }
                .child-table-container {
                    background-color: #ffffff;
                    padding: 12px 16px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    margin: 8px 0 8px 30px;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
                }
                .child-table th {
                    background-color: #f8fafc !important;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #475569;
                    border-bottom: 2px solid #cbd5e1 !important;
                }
                .child-table td {
                    padding: 8px 12px !important;
                }
            `}</style>

            <div className="d-flex justify-content-end mb-2 px-1">
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={toggleExpandAll}
                    style={{ borderRadius: '6px', fontWeight: '500' }}
                    disabled={paginatedGroups.length === 0}
                >
                    <i className={`fas ${allExpanded ? 'fa-compress-alt' : 'fa-expand-alt'} mr-1`}></i>
                    {allExpanded ? 'Thu gọn tất cả' : 'Mở rộng tất cả'}
                </Button>
            </div>

            <div className="table-responsive rounded-lg shadow-xs border bg-white">
                <Table className="mb-0" style={{ fontSize: '0.9rem', verticalAlign: 'middle' }}>
                    <thead className="bg-light">
                        <tr>
                            <th style={{ width: '80px' }} className="text-center">STT</th>
                            <th style={{ width: '220px' }}>Khoảng thời gian</th>
                            <th>Đơn vị / Khoa</th>
                            <th style={{ color: 'var(--primary)' }}>Giảng viên hướng dẫn (GVLS)</th>
                            <th style={{ width: '180px' }} className="text-center">Số học viên</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedGroups.map((group, index) => {
                            const isExpanded = !!expandedKeys[group.key];
                            const tutorDisplay = group.tutors.join(', ') || 'Chưa phân công';
                            return (
                                <React.Fragment key={group.key}>
                                    <tr 
                                        className="parent-row" 
                                        onClick={() => toggleGroup(group.key)}
                                    >
                                        <td className="text-center text-muted">
                                            <i className={`fas fa-chevron-right mr-2 toggle-icon ${isExpanded ? 'expanded' : ''}`}></i>
                                            {(currentPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="font-weight-bold text-secondary">
                                            {group.dateRangeStr}
                                        </td>
                                        <td className="font-weight-bold text-dark">{group.donVi}</td>
                                        <td>
                                            <span className="badge bg-light text-primary border p-2">
                                                <i className="fas fa-user-md mr-1"></i> {tutorDisplay}
                                            </span>
                                        </td>
                                        <td className="text-center font-weight-bold text-teal" style={{ color: '#0d9488' }}>
                                            {group.students.length} học viên
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan="5" className="bg-light p-0">
                                                <div className="child-table-container">
                                                    <div className="font-weight-bold mb-2 text-secondary small">
                                                        <i className="fas fa-graduation-cap mr-1"></i> DANH SÁCH CHI TIẾT HỌC VIÊN PHÂN CÔNG ĐỢT NÀY:
                                                    </div>
                                                    <Table size="sm" bordered hover className="child-table mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '50px' }} className="text-center">STT</th>
                                                                <th>Họ và tên</th>
                                                                <th>Trình độ</th>
                                                                <th>Giảng viên hướng dẫn</th>
                                                                <th style={{ width: '150px' }}>Trạng thái</th>
                                                                <th style={{ width: '120px' }} className="text-center">Hành động</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {group.students.map((student, sIdx) => {
                                                                const studentGvs = student.lichHocThucHanh_GiangViens || student.LichHocThucHanh_GiangViens || [];
                                                                const studentGvNames = studentGvs.map(g => g.giangVienTen || g.GiangVienTen).join(', ') || 'Chưa phân công';
                                                                return (
                                                                    <tr key={student.id || student.Id}>
                                                                        <td className="text-center text-muted">{sIdx + 1}</td>
                                                                        <td className="font-weight-bold text-dark">{student.hocVienTen || student.HocVienTen}</td>
                                                                        <td>{student.trinhDo || student.TrinhDo || '-'}</td>
                                                                        <td>
                                                                            <span className="badge bg-light text-primary border px-2 py-1">
                                                                                <i className="fas fa-user-md mr-1"></i> {studentGvNames}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            {student.trangThai === 1 && <Badge bg="success">Đã xác nhận</Badge>}
                                                                            {student.trangThai === 2 && (
                                                                                <Badge bg="warning" text="dark" style={{ cursor: 'help' }} title={student.ghiChu || student.GhiChu}>
                                                                                    <i className="fas fa-exclamation-triangle mr-1"></i> Cảnh báo
                                                                                </Badge>
                                                                            )}
                                                                            {student.trangThai === 3 && (
                                                                                <Badge bg="danger" style={{ cursor: 'help' }} title={student.ghiChu || student.GhiChu}>
                                                                                    <i className="fas fa-times-circle mr-1"></i> Lỗi
                                                                                </Badge>
                                                                            )}
                                                                            {(student.trangThai === 0 || !student.trangThai) && <Badge bg="secondary">Nháp</Badge>}
                                                                        </td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            className="mr-1 py-0 px-2"
                                                                            style={{ borderRadius: '4px' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenEdit(student);
                                                                            }}
                                                                        >
                                                                            <i className="fas fa-edit small"></i> Sửa
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            className="py-0 px-2"
                                                                            style={{ borderRadius: '4px' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteRow(student.id || student.Id);
                                                                            }}
                                                                        >
                                                                            <i className="fas fa-trash-alt small"></i> Xóa
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {groupedData.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center py-4 text-muted">Không tìm thấy kết quả phù hợp với bộ lọc.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 px-1">
                <div className="text-muted small">
                    Hiển thị {paginatedGroups.length} trên {groupedData.length} nhóm đợt thực hành.
                </div>
                {totalPages > 1 && (
                    <Pagination size="sm" className="mb-0" style={{ borderRadius: '6px' }}>
                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else {
                                pageNum = currentPage - 2 + i;
                                if (currentPage <= 2) pageNum = i + 1;
                                if (currentPage >= totalPages - 1) pageNum = totalPages - 4 + i;
                            }
                            if (pageNum < 1) pageNum = 1;
                            if (pageNum > totalPages) return null;
                            
                            return (
                                <Pagination.Item
                                    key={pageNum}
                                    active={pageNum === currentPage}
                                    onClick={() => setCurrentPage(pageNum)}
                                >
                                    {pageNum}
                                </Pagination.Item>
                            );
                        })}
                        
                        <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                )}
            </div>
        </>
    );
};

export default DetailedScheduleListView;
